import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const clearAuthStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('company');
  delete axios.defaults.headers.common['Authorization'];
};

const safeParse = (value) => {
  if (!value || value === 'null' || value === 'undefined') return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = safeParse(localStorage.getItem('user'));
      const storedCompany = safeParse(localStorage.getItem('company'));

      if (token && storedUser && typeof storedUser === 'object') {
        setUser(storedUser);
        if (storedUser.role !== 'superadmin') {
          setCompany(storedCompany);
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else if (token || localStorage.getItem('user') || localStorage.getItem('company')) {
        // Corrupt/partial auth data — clear so the app can render
        clearAuthStorage();
      }
    } catch (e) {
      console.error('Failed to restore auth session:', e);
      clearAuthStorage();
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/api/auth/login', { email, password });

    if (response.data.require2FA) {
      return response.data;
    }

    const { token, user, company } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    if (company) {
      localStorage.setItem('company', JSON.stringify(company));
      setCompany(company);
    } else {
      localStorage.removeItem('company');
      setCompany(null);
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
    return response.data;
  };

  const setAuthData = (data) => {
    const { token, user, company } = data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    if (company) {
      localStorage.setItem('company', JSON.stringify(company));
      setCompany(company);
    } else {
      localStorage.removeItem('company');
      setCompany(null);
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
  };

  const register = async (userData) => {
    const response = await axios.post('/api/auth/register', userData);
    const { token, user, company } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('company', JSON.stringify(company));

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
    setCompany(company);
    return response.data;
  };

  const logout = () => {
    clearAuthStorage();
    setUser(null);
    setCompany(null);
  };

  const value = {
    user,
    company,
    setCompany,
    login,
    register,
    logout,
    setAuthData,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
